import type { T_Optional } from '@tryiris-ai/js-types';
import { isNil } from '@tryiris-ai/js-types';
import type { CloseReason, Fragment, Notification, Options } from './types.js';
import { clear, isClosing, isPending, post, subscribeNotificationLifecycle } from './utils.js';
import { depot } from '#~/depot/index.js';
import { isNotificationStackHidden } from './stack-visibility.js';

// Incident helper over the notification store (ENG-4930 item 1). An incident is
// one *problem*, keyed by alert + entity + audience (never the display text).
// Each display of it is a *showing* with a fresh UUID that analytics carries on
// shown / closed / acted (contract: ENG-4874). Suppression state is in memory
// only: nothing survives a reload, sign-out or restart (decided Oct 28).
// New consumers only; existing `post()` call sites are untouched.

/** Alert identifiers from the initiative (`alert_id` in ENG-4874). */
export type AlertId =
  | 'n1'
  | 'n2'
  | 'n3'
  | 'n4'
  | 'n5'
  | 'n6'
  | 'n7'
  | 'n8'
  | 'n9'
  | 'n10'
  | 'n11'
  | 'n12';

/** What makes a problem distinct. */
export interface IncidentKey {
  readonly alert: AlertId;
  /** The thing the problem is about, e.g. a Bridge or camera id. */
  readonly entity?: string;
  /** Who the message is for, when the same problem is told to different people. */
  readonly audience?: string;
}

/**
 * Showing lifecycle. `dismissed` covers both the close X and the auto-close
 * timer (see `closeReason`). `unknown` means the message left the store without
 * telling the helper why (an old `clear()` caller, `clearAll()` on sign-out).
 */
export type IncidentState = 'showing' | 'dismissed' | 'resolved' | 'superseded' | 'unknown';

/** One problem and its current (or last) showing. */
export interface Incident extends IncidentKey {
  readonly key: string;
  /** Alert version, e.g. N5 `recording` / `not_recording` (`alert_variant`). */
  readonly variant?: string;
  /** New UUID per display (`showing_id`). */
  readonly showingId: string;
  readonly notificationId: Notification['id'];
  readonly state: IncidentState;
  readonly closeReason?: CloseReason;
  /** First visible mount time; absent while queued or hidden behind the session prompt. */
  readonly shownAt?: number;
}

/** What the helper tells listeners (ENG-4874 hangs its events on these). */
export type IncidentEvent =
  | { readonly type: 'shown'; readonly incident: Incident }
  | { readonly type: 'closed'; readonly incident: Incident; readonly reason: CloseReason }
  | { readonly type: 'acted'; readonly incident: Incident; readonly action: string };

/** `raise()` options: notice options plus the alert's own rules. */
export type RaiseOptions = Options &
  T_Optional<{
    /** Alert version recorded on the incident. */
    variant: string;
    /** Minimum time between showings of the same problem (default 60 seconds). */
    cooldown: number;
    /** Start cooldown at first visibility (default) or dismissal, including auto-close. */
    cooldownFrom: 'shown' | 'dismissed';
    /** Show at most once per app run or page load (N10 per Bridge, N12). */
    once: boolean;
  }>;

/** Default "not again within" period between showings of one problem. */
export const DefaultCooldown = 60_000;

const NotificationIdPrefix = 'alert:';

const incidents = new Map<string, Incident>();
// Keys that must never show again this run (`once`), kept across their incident.
const spent = new Set<string>();
// Queued/hidden notices consume no suppression and emit no lifecycle until visible.
const shownByKey = new Map<string, string>();
const cooldownsByKey = new Map<string, { from: 'shown' | 'dismissed'; since?: number }>();
const listeners = new Set<(event: IncidentEvent) => void>();

const emit = (event: IncidentEvent): void => {
  for (const listener of listeners) listener(event);
};

/**
 * Stable problem key: alert + entity + audience.
 *
 * @param key - Problem identity.
 * @returns Joined key string.
 */
export const toIncidentKey = ({ alert, audience, entity }: IncidentKey): string =>
  [alert, entity ?? '', audience ?? ''].join(':');

/**
 * Mint a showing ID (`showing_id`): a UUID, new per display.
 *
 * @returns Random UUID string.
 */
export const newShowingId = (): string => {
  const c = typeof crypto === 'undefined' ? undefined : crypto;

  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID();
  }

  // RFC 4122 v4 from random bytes for runtimes without `randomUUID`.
  const bytes = new Uint8Array(16);

  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }

  bytes[6] = (bytes[6]! & 0x0f) | 0x40;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;

  const hex = [...bytes].map((byte) => byte.toString(16).padStart(2, '0')).join('');

  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

// A showing is live while its notice is open in the store (or its delayed post
// is still pending). Otherwise something outside the helper removed it.
const refresh = (incident: Incident): Incident => {
  if (
    incident.state !== 'showing' ||
    isPending(incident.notificationId) ||
    isClosing(incident.notificationId)
  ) {
    return incident;
  }

  const entry = depot.getState().notifications[incident.notificationId];

  if (isNil(entry) || entry[2].isClosed) {
    const next: Incident = { ...incident, state: 'unknown' };
    incidents.set(incident.key, next);
    return next;
  }

  return incident;
};

const current = (key: string): Incident | undefined => {
  const incident = incidents.get(key);

  return incident && refresh(incident);
};

/**
 * Current (or last) incident for a problem.
 *
 * @param key - Problem identity.
 * @returns The incident, or `undefined` if the problem was never raised this run.
 */
export const getIncident = (key: IncidentKey): Incident | undefined => current(toIncidentKey(key));

/**
 * Show a problem once. Returns the live showing if the problem is already on
 * screen, `undefined` if it is suppressed (cooldown or `once`), otherwise posts
 * the notice and returns the new showing. This does not update a live showing's
 * text or variant: consumers must explicitly end it before raising a replacement.
 * `shownAt`, the shown event and suppression start on the first visible mount,
 * not store delivery. Read the event/getIncident snapshot for that timestamp.
 *
 * @param key - Problem identity.
 * @param notification - What to show; the id is derived from the key.
 * @param options - Notice options plus the alert's own rules.
 * @returns The showing, or `undefined` when suppressed.
 */
export const raise = (
  key: IncidentKey,
  notification: Omit<Fragment, 'id'>,
  options?: RaiseOptions
): Incident | undefined => {
  const {
    cooldown = DefaultCooldown,
    cooldownFrom = 'shown',
    once = false,
    variant,
    onShown,
    ...noticeOptions
  } = options ?? {};
  const k = toIncidentKey(key);
  const existing = current(k);

  // One message per problem.
  if (existing?.state === 'showing') {
    return existing;
  }

  if (once && spent.has(k)) {
    return undefined;
  }

  const cooldownSince = cooldownsByKey.get(k)?.since;
  if (cooldownSince !== undefined && Date.now() - cooldownSince < cooldown) {
    return undefined;
  }

  const notificationId = `${NotificationIdPrefix}${k}`;

  const incident: Incident = {
    ...key,
    key: k,
    notificationId,
    showingId: newShowingId(),
    state: 'showing',
    ...(isNil(variant) ? {} : { variant })
  };

  incidents.set(k, incident);
  cooldownsByKey.delete(k);
  post(
    { ...notification, id: notificationId },
    {
      ...noticeOptions,
      onShown: () => {
        const active = current(k);
        // A late mount from a cancelled/replaced showing must never spend the
        // replacement's suppression or claim that its different message was shown.
        if (
          active?.showingId !== incident.showingId ||
          active.state !== 'showing' ||
          isPending(notificationId) ||
          isNotificationStackHidden() ||
          shownByKey.get(k) === incident.showingId
        )
          return;
        const visible: Incident = { ...active, shownAt: Date.now() };
        incidents.set(k, visible);
        shownByKey.set(k, incident.showingId);
        // Dismissal-based alerts start their clock only when dismissed. Producer
        // cleanup (supersede on unmount/flag-off) must not silence an undismissed warning.
        cooldownsByKey.set(k, {
          from: cooldownFrom,
          ...(cooldownFrom === 'shown' ? { since: visible.shownAt! } : {})
        });
        if (once) spent.add(k);
        emit({ type: 'shown', incident: visible });
        onShown?.();
      }
    }
  );

  return incident;
};

const closeShowing = (
  k: string,
  state: Extract<IncidentState, 'resolved' | 'superseded'>,
  reason: CloseReason
): void => {
  const incident = current(k);

  if (incident?.state !== 'showing') {
    return;
  }

  const next: Incident = { ...incident, closeReason: reason, state };
  const wasShown = shownByKey.get(k) === incident.showingId;
  // Publish the terminal state before clearing: a synchronous subscriber must
  // not turn the old showing unknown or have its replacement overwritten.
  incidents.set(k, next);
  clear(incident.notificationId);
  if (wasShown) {
    emit({ type: 'closed', incident: next, reason });
  }
};

/**
 * The problem went away: close its message by itself, with no "it's fixed"
 * notice. By default, no-op unless the problem is showing.
 *
 * @param key - Problem identity.
 * @param options - Opt into resetting this incident's cooldown on recovery,
 * even after dismissal. Never resets once-per-run suppression or closes a display twice.
 */
export const resolve = (key: IncidentKey, options?: { readonly resetCooldown?: boolean }): void => {
  const k = toIncidentKey(key);
  if (options?.resetCooldown) {
    cooldownsByKey.delete(k);
    const incident = current(k);
    // Recovery after dismissal changes problem state, not the already-emitted close event.
    if (incident && incident.state !== 'showing') {
      incidents.set(k, { ...incident, state: 'resolved', closeReason: 'resolved' });
    }
  }
  closeShowing(k, 'resolved', 'resolved');
};

/**
 * Something else replaced the message (for example the session pop-up).
 * No-op unless the problem is showing.
 *
 * @param key - Problem identity.
 */
export const supersede = (key: IncidentKey): void => {
  closeShowing(toIncidentKey(key), 'superseded', 'superseded');
};

/**
 * Listen for showings being shown, closed (with why) and acted on.
 *
 * @param listener - Called with each incident event.
 * @returns Unsubscribe function.
 */
export const subscribeIncidents = (listener: (event: IncidentEvent) => void): (() => void) => {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
};

/** Forget every incident and suppression, e.g. on sign-out. */
export const resetIncidents = (): void => {
  for (const incident of incidents.values()) {
    if (isPending(incident.notificationId)) clear(incident.notificationId);
  }
  incidents.clear();
  shownByKey.clear();
  cooldownsByKey.clear();
  spent.clear();
};

// Raw lookup, without `refresh`: the store has already marked the notice closed
// when a lifecycle event arrives, and the event itself says why.
const byNotificationId = (id: Notification['id']): Incident | undefined =>
  typeof id === 'string' && id.startsWith(NotificationIdPrefix)
    ? incidents.get(id.slice(NotificationIdPrefix.length))
    : undefined;

// The panel reports the close X and auto-close through the store's lifecycle
// listener (ENG-4930 item 2); actions report through the same channel.
subscribeNotificationLifecycle((event) => {
  const incident = byNotificationId(event.id);

  if (!incident) {
    return;
  }

  if (event.type === 'acted') {
    if (shownByKey.get(incident.key) === incident.showingId) {
      emit({ type: 'acted', incident, action: event.action });
    }
    return;
  }

  if (incident.state !== 'showing') {
    return;
  }

  const next: Incident = { ...incident, closeReason: event.reason, state: 'dismissed' };
  incidents.set(incident.key, next);
  if (shownByKey.get(incident.key) === incident.showingId) {
    const cooldown = cooldownsByKey.get(incident.key);
    if (cooldown?.from === 'dismissed') {
      cooldownsByKey.set(incident.key, { ...cooldown, since: Date.now() });
    }
    emit({ type: 'closed', incident: next, reason: event.reason });
  }
});
